import { PayloadAction, createSlice, createSelector } from "@reduxjs/toolkit";
import { getTodayBackend } from "../utils/dateUtils";
import { ThunkAction } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import type { RootState } from "./store";

export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, AnyAction>;

export type SubscriptionType = "monthly" | "yearly";

// Define a type for the slice state
interface StorageUnitState {
  id: string;
  level: string;
  city: string;
  address: string;
  size: number;
  country: string;
  language: string;
  defaultDiscountCode: string;
  discountCode: string;
  discountPlanData: any;
  startDate: any;
  prebookDaysMaximum: number;
  subscriptionType: SubscriptionType;
  price: number;
  insurancePrice: number;
  transportationPrice: number;
  currentTotalPrice: number;
}

// Define the initial state using that type
const initialState: StorageUnitState = {
  id: "",
  level: "",
  city: "",
  address: "",
  size: 0,
  country: "",
  language: "",
  defaultDiscountCode: "",
  discountCode: "",
  discountPlanData: {},
  startDate: getTodayBackend(),
  prebookDaysMaximum: 21,
  subscriptionType: "monthly",
  price: 0,
  insurancePrice: 0,
  transportationPrice: 0,
  currentTotalPrice: 0,
};

export const storageUnitSlice = createSlice({
  name: "storageUnit",
  initialState,
  reducers: {
    setId: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
    setLevel: (state, action: PayloadAction<string>) => {
      state.level = action.payload;
    },
    setCity: (state, action: PayloadAction<string>) => {
      state.city = action.payload;
    },
    setAddress: (state, action: PayloadAction<string>) => {
      state.address = action.payload;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
    },
    setCountry: (state, action: PayloadAction<string>) => {
      state.country = action.payload;
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    setDefaultDiscountCode: (state, action: PayloadAction<string>) => {
      state.defaultDiscountCode = action.payload;
    },
    setDiscountCode: (state, action: PayloadAction<string>) => {
      state.discountCode = action.payload;
    },
    setDiscountPlanData: (state, action: PayloadAction<any>) => {
      state.discountPlanData = action.payload;
    },
    setStartDate: (state, action: PayloadAction<any>) => {
      state.startDate = action.payload;
    },
    setPrebookDaysMaximum: (state, action: PayloadAction<number>) => {
      state.prebookDaysMaximum = action.payload;
    },
    setSubscriptionType: (state, action: PayloadAction<SubscriptionType>) => {
      state.subscriptionType = action.payload;
    },
    setPrice: (state, action: PayloadAction<number>) => {
      state.price = action.payload;
    },
    setInsurancePrice: (state, action: PayloadAction<number>) => {
      state.insurancePrice = action.payload;
    },
    setTransportationPrice: (state, action: PayloadAction<number>) => {
      state.transportationPrice = action.payload;
    },
    setCurrentTotalPrice: (state, action: PayloadAction<number>) => {
      state.currentTotalPrice = action.payload;
    },
  },
});

export const {
  setId,
  setLevel,
  setCity,
  setAddress,
  setSize,
  setCountry,
  setLanguage,
  setDefaultDiscountCode,
  setDiscountCode,
  setDiscountPlanData,
  setStartDate,
  setPrice,
  setPrebookDaysMaximum,
  setSubscriptionType,
  setInsurancePrice,
  setTransportationPrice,
  setCurrentTotalPrice,
} = storageUnitSlice.actions;

// Other code such as selectors can use the imported `RootState` type
// export const selectStorageUnit = (state: RootState) => state.storageUnit;
export const selectSubscriptionType = (state: any) => state.storageUnit.subscriptionType;
export const selectStorageUnitPrice = (state: any) => state.storageUnit.price;
export const selectDiscountPercentage = (state: any) => state.storageUnit.discountPlanData?.defaultValue || 0;
export const selectInsurancePrice = (state: any) => state.storageUnit.insurancePrice;
export const selectTransportationPrice = (state: any) => state.storageUnit.transportationPrice;

export const selectCalculatedPrices = createSelector(
  selectSubscriptionType,
  selectStorageUnitPrice,
  selectDiscountPercentage,
  selectInsurancePrice,
  selectTransportationPrice,
  (subscriptionType, price, discountPercentage, insurancePrice, transportation) => {
    const priceMultiplier = subscriptionType === "monthly" ? 1 : 12;

    const basePrice = (price * priceMultiplier).toFixed(2);
    const discountedBasePrice = (Number(basePrice) - Number(basePrice) * discountPercentage).toFixed(2);
    const basePriceDiscount = (Number(basePrice) - Number(discountedBasePrice)).toFixed(2);

    const recurringInsurancePrice = (insurancePrice * priceMultiplier).toFixed(2);

    const recurringTotal = ((price + insurancePrice) * priceMultiplier).toFixed(2);
    const recurringDiscountedTotal = ((price - price * discountPercentage + insurancePrice) * priceMultiplier).toFixed(2);

    return {
      basePrice,
      discountedBasePrice,
      basePriceDiscount,
      recurringTotal,
      recurringDiscountedTotal,
      recurringInsurancePrice,
      discountedTotal: recurringDiscountedTotal,
      totalDiscount: basePriceDiscount,
    };
  }
);

export const updateDiscountCode = (code: string): ThunkAction<void, any, unknown, AnyAction> => (dispatch) => {
  dispatch(setDiscountPlanData({}));
  dispatch(setDiscountCode(""));
  // Use Promise.resolve().then() to ensure the state change is observed correctly by React Query or similar tools.
  Promise.resolve().then(() => {
    dispatch(setDiscountCode(code));
  });
};

export default storageUnitSlice.reducer;
